import { Helper } from "../../core/util/helper.js";
import { GridCustomToolBarButton, ToolBar, ToolBarCustomButton, ToolBarStandardButton, ToolBarStandardButtonType } from "./toolBar.js";
import React from "react";
import ReactDOM from "react-dom";
export enum ExpandOnInitState {
    None,
    FirstNodes,
    AllNodes
}

interface KendoTreeState {
    filterValue: string;
    isLoading: boolean;
    selectedNode: any;
    expandedNodesIds: any[];
    expandedOnInit: boolean;
}

interface KendoTreeProps {
    treeData: TreeData[];
    template: (dataItem) => string;
    onSelectionChange?: (dataItem) => void;
    callAfterDataSourceInit?: () => void;
    loadOnDemand?: boolean;
    refreshOnTreeDataChanged?: boolean;
    showExpandAndCollapseIcons?: boolean;
    expandOnInitState?: ExpandOnInitState;
    standardButtons?: Array<ToolBarStandardButton>;
    customButtons?: Array<GridCustomToolBarButton>;
    onClickStandardButton?(type: any, dataItem: any, allItems?: Array<any>);
    onClickCustomButton?(id: string, dataItem: any, allItems?: Array<any>);
    filter?: kendo.data.DataSourceFilters;
    filterApp?: React.ReactNode;
    autoSelectFirstElement?: boolean;
    selectedNodeId?: any;
    showSelectFirstNodeIcon?: boolean;
}

export class KendoTree extends React.Component<KendoTreeProps, KendoTreeState>{
    private treeElementRef: any;
    private treeRef: kendo.ui.TreeView;
    private treeId = kendo.guid();

    constructor(props) {
        super(props);
        this.state = {
            filterValue: "",
            isLoading: true,
            selectedNode: null,
            expandedNodesIds: [],
            expandedOnInit: false
        }
    }

    componentDidMount() {
        this.createKendoTree();

    }

    componentDidUpdate(prevProps, prevState) {
        let treeDataChanged = prevProps.treeData != this.props.treeData && this.props.refreshOnTreeDataChanged;
        let filterChanged = JSON.stringify(prevProps.filter) != JSON.stringify(this.props.filter);

        if (treeDataChanged || filterChanged) {
            if (Helper.hasValue(this.treeRef) && Helper.hasValue(this.props.treeData)) {
                this.refreshDataSource();
                this.expandNodes(this.state.expandedNodesIds);
                this.selectNode(this.state.selectedNode?.id);
            }
        }

        if (this.props.selectedNodeId != prevProps.selectedNodeId && this.state.selectedNode?.Id != this.props.selectedNodeId) {
            this.selectNode(this.props.selectedNodeId);
        }
    }

    private refreshDataSource() {
        let dataSource = this.getDataSource();
        dataSource.data(this.props.treeData);
        if (Helper.hasValue(this.props.filter))
            dataSource.filter(this.props.filter);
        this.treeRef.setDataSource(dataSource);
    }

    public addNode(data: any, parentId) {
        let node = this.getNodeById(parentId);
        this.treeRef.append(data, node);
    }

    public removeNode(id) {
        let node = this.getNodeById(id);
        this.treeRef.remove(node);
    }

    public setNodeValue(node: JQuery<HTMLElement>, name: string, value: any) {
        this.treeRef.dataItem(node).set(name, value);
    }

    private getSelectedNodeId() {
        let selectedNode = this.treeRef.select();
        let item = this.getDataItemByNode(selectedNode);
        return item?.id;
    }

    public selectNode(id) {
        let node = this.getNodeById(id);
        if (Helper.hasValue(node)) {
            let selectedNode = this.getDataItemByNode(node);
            this.setState({ selectedNode: selectedNode }, () => {
                this.treeRef.select(node);
                if (Helper.hasValue(this.props.onSelectionChange)) {
                    this.props.onSelectionChange(selectedNode);
                }
            })
        } else {
            this.setState({ selectedNode: null }, () => {
                this.treeRef.select($());
            })
        }
    }

    public getSelected() {
        var selected = this.treeRef.select();
        return this.getDataItemByNode(selected);
    }

    public getDataSource() {
        return this.treeRef.dataSource;
    }

    public getData() {
        return this.treeRef.dataSource.data();
    }

    public getEntries(): any {
        let currentItems = this.treeRef.dataSource.data();
        return currentItems;
    }

    private collapseAll = () => {
        this.treeRef.collapse(".k-item");
    }

    private expandAll = () => {
        this.treeRef.expand(".k-item");
    }

    private expandFirstNodes = () => {
        this.treeRef.expand("> .k-group > .k-item");
    }

    private expandNodes = (ids: any[]) => {
        if (ids == null)
            return;

        for (let id of ids) {
            let node = this.getNodeById(id);
            this.treeRef.expand(node);
        }
    }

    public sync() {
        this.treeRef.dataSource.sync();
    }

    public filter(filter) {
        this.treeRef.dataSource.filter(filter);
    }

    public getDataItemById(id) {
        if (!Helper.hasValue(id) || id == 0)
            return null;
        let dataItem = this.treeRef.dataSource.get(id);
        return dataItem;
    }

    public getDataItemByNode(node) {
        return this.treeRef.dataItem(node);
    }

    public getNodeById(id) {
        if (!Helper.hasValue(id) || id == 0)
            return null;
        let dataItem = this.treeRef.dataSource.get(id);
        if (!Helper.hasValue(dataItem))
            return null;
        let node = this.treeRef.findByUid(dataItem.uid);
        return node;
    }

    private removeExpandedNodeId = (id: any) => {
        let expandedNodeIds = this.state.expandedNodesIds;
        expandedNodeIds = expandedNodeIds.filter(x => x != id);
        this.setState({ expandedNodesIds: expandedNodeIds });
    }

    private addExpandedNodeId = (id: any) => {
        let expandedNodeIds = this.state.expandedNodesIds;
        if (expandedNodeIds.find(x => x == id) == null)
            expandedNodeIds.push(id);
        this.setState({ expandedNodesIds: expandedNodeIds });
    }

    private expandOnInit = () => {
        if (this.state.expandedOnInit)
            return;

        if (Helper.hasValue(this.props.expandOnInitState)) {
            switch (this.props.expandOnInitState) {
                case ExpandOnInitState.FirstNodes:
                    this.expandFirstNodes();
                    break;
                case ExpandOnInitState.AllNodes:
                    this.expandAll();
                    break;
            }
        }

        this.setState({ expandedOnInit: true });
    }

    private selectFirstElement = () => {
        if (Helper.hasValue(this.props.treeData) && this.props.treeData.length > 0) {
            let item = this.props.treeData[0];
            this.selectNode(item.Id);
        }
    }

    private createKendoTree = () => {
        this.treeRef = $(this.treeElementRef).kendoTreeView({
            dataSource: {
                data: this.props.treeData ?? [],
                schema: {
                    model: {
                        id: "Id",
                        children: "Children"
                    }
                },
                filter: this.props.filter ?? null
            },
            template: (dataItem) => this.props.template(dataItem),
            loadOnDemand: this.props.loadOnDemand ?? false,
            select: (e) => {
                let item = this.getDataItemByNode(e.node);
                this.setState({ selectedNode: item }, () => {
                    if (Helper.hasValue(this.props.onSelectionChange)) {
                        this.props.onSelectionChange(item);
                    }
                });
            },
            dataBound: (e) => {
                if (Helper.hasValue(this.treeRef)) {
                    this.expandOnInit();
                }
            },
            collapse: (e) => {
                let dataItem = this.getDataItemByNode(e.node);
                if (dataItem == null)
                    return;
                this.removeExpandedNodeId(dataItem.id);
            },
            expand: (e) => {
                let dataItem = this.getDataItemByNode(e.node);
                if (dataItem == null)
                    return;
                this.addExpandedNodeId(dataItem.id);
            }
        }).data("kendoTreeView");

        this.setState({}, this.callAfterDataSourceInit);
    }

    private callAfterDataSourceInit = () => {
        if (this.props.autoSelectFirstElement && !this.props.loadOnDemand) {
            this.selectFirstElement();
        }
        if (this.props.selectedNodeId) {
            this.selectNode(this.props.selectedNodeId);
        }
        if (Helper.hasValue(this.props.callAfterDataSourceInit)) {
            this.props.callAfterDataSourceInit();
        }
    }

    private getIconBar() {
        let iconExpand = null;
        let iconCollapse = null;
        let iconUnselectNode = null;

        if (this.props.showExpandAndCollapseIcons) {
            iconExpand = this.getIcon(this.expandAll, "fal fa-arrow-alt-down", Helper.getTranslation("MonitorBaum.AlleAufklappen"));
            iconCollapse = this.getIcon(this.collapseAll, "fal fa-arrow-alt-up", Helper.getTranslation("MonitorBaum.AlleZuklappen"));
        }

        if (this.props.showSelectFirstNodeIcon) {
            iconUnselectNode = this.getIcon(this.selectFirstElement, "fal fa-left-to-line", Helper.getTranslation("MonitorBaum.SelectFirst"));
        }

        let filter = null;
        if (this.props.filterApp) {
            filter = <div className="skills-icon-bar__icon-wrapper">
                <Popup icon="fal fa-filter skills-icon-bar__icon" title="filter">
                    {this.props.filterApp}
                </Popup>
            </div>;
        }

        return (<div style={{ display: "flex", margin: "5px" }} >
            <div className="skills-icon-bar">
                {iconExpand}
                {iconCollapse}
                {iconUnselectNode}
            </div >
            {filter}
        </div>);
    }

    private getIcon = (onClick: () => void, iconName: string, title: string) => {
        return (<div onClick={onClick} className="skills-icon-bar__icon-wrapper" title={title} style={{ height: "36px", width: "36px", display: "flex", justifyContent: "center", alignItems: "center", margin: "0" }}>
            <i className={iconName + " skills-icon-bar__icon"} />
        </div>);
    }

    private _handleStandardButtonClick = (type: ToolBarStandardButtonType) => {
        if (this.props.onClickStandardButton) {
            this.props.onClickStandardButton(type, this.state.selectedNode);
        }
    }

    private _handleCustomButtonClick = (id: string) => {
        if (this.props.onClickCustomButton) {
            this.props.onClickCustomButton(id, this.state.selectedNode);
        }
    }

    render() {
        const stdBtn: Array<ToolBarStandardButton> = [], cusBtn: Array<ToolBarCustomButton> = [];

        if (Array.isArray(this.props.standardButtons)) {
            for (let i = 0; i < this.props.standardButtons.length; i++) {
                const btn = this.props.standardButtons[i];
                stdBtn.push({
                    caption: btn.caption,
                    enabled: btn.enabled && (btn.type === ToolBarStandardButtonType.EDIT || btn.type === ToolBarStandardButtonType.DELETE ? this.state.selectedNode !== null : true),
                    type: btn.type
                });
            }
        }

        if (Array.isArray(this.props.customButtons)) {
            for (let i = 0; i < this.props.customButtons.length; i++) {
                const btn = this.props.customButtons[i];
                cusBtn.push({
                    caption: btn.caption,
                    enabled: btn.enabled && (!btn.autoEnabledOnRecordSelected || this.state.selectedNode !== null),
                    iconClass: btn.iconClass,
                    id: btn.id,
                    width: btn.width
                });
            }
        }

        let iconBar = this.getIconBar();

        return (<>
            <div className="flex-container">
                <ToolBar standardButtons={stdBtn} customButtons={cusBtn} handleStandardButtonClick={this._handleStandardButtonClick} handleCustomButtonClick={this._handleCustomButtonClick} />
                {iconBar}
                <div className="flex-container flex-container__item" style={{ overflowX: "hidden" }} ref={(ref) => { this.treeElementRef = ref; }} id={this.treeId} />
            </div>
        </>);
    }
}

class Popup extends React.Component<{
    icon?: string;
    title?: string;
    children?: React.ReactNode;
}, any>{
    private anchorRef: HTMLElement;
    private popupRef: HTMLElement;
    private popup: kendo.ui.Popup;
    private root: any;

    constructor(props) {
        super(props);
    }

    componentDidMount() {
        this.popup = $(this.popupRef).kendoPopup({
            anchor: $(this.anchorRef)
        }).data("kendoPopup");

        this.root = ReactDOM["createRoot"](this.popupRef);
        this.root.render(this.props.children);
    }

    render() {
        return (<>
            <div ref={(e) => { this.anchorRef = e }} >
                <div onClick={() => this.popup.toggle()}>
                    <i className={this.props.icon} title={this.props.title} />
                </div>
            </div>
            <div ref={(e) => { this.popupRef = e }} />
        </>)
    }
}

export interface TreeData {
    Id?: any;
    Children?: TreeData[];
}